// Orders list — role-scoped server-side (client: their company's; rep: their book's; staff: all).
// Internal staff can soft-archive genuinely-rejected orders (bulk) to keep the list clean, and
// switch to an Archived view to review or restore them. Never a hard delete.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import StoreSwitcher from '../components/shell/StoreSwitcher.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import { listOrdersPage, ordersCsvUrl, archiveOrders, restoreOrders } from '../api/orders.js';
import Pager from '../components/Pager.jsx';
import { fmtOrderTotal } from '../lib/matrix.js';
import { useAuthStore } from '../stores/authStore.js';
import { useStoreContext } from '../stores/storeContext.js';
import { storeShort } from '../lib/stores.js';
import { orderStatusLabel, paymentStatusLabel } from '../lib/labels.js';
import { approvalReasons } from '../lib/approval.js';

const FILTER_KEYS = ['', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'CANCELLED'];
const PAGE_SIZE = 50;

// Status + payment + (staff) next-step chips for one order — shared by the table and the phone cards.
function OrderChips({ o, isStaff }) {
  return (
      <div className="flex flex-wrap items-center gap-1">
        <StatusBadge status={o.status} />
        {o.paymentStatus === 'PAID_IN_FULL' && (
          <span className="rounded-full bg-success-light px-1.5 py-0.5 text-[10px] font-medium text-success">{paymentStatusLabel('PAID_IN_FULL')}</span>
        )}
        {o.paymentStatus === 'DEPOSIT_PAID' && (
          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">{paymentStatusLabel('DEPOSIT_PAID')}</span>
        )}
        {/* Staff: what a pending order is waiting on, at a glance. */}
        {isStaff && o.status === 'PENDING_APPROVAL' && (
          <span
            className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${o.shippingState === 'CONFIRMED' ? 'bg-success-light text-success' : 'bg-accent-light text-text-primary'}`}
            title="Next step"
          >
            {o.shippingState === 'CONFIRMED' ? 'Ready to approve' : 'Confirm shipping'}
          </span>
        )}
        {isStaff &&
          o.status === 'PENDING_APPROVAL' &&
          approvalReasons(o.approvalReason)
            .filter((r) => r.code !== 'manual_review')
            .map((r) => (
              <span key={r.code} className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${r.credit ? 'bg-error-light text-error' : 'bg-surface-raised text-text-secondary'}`}>
                {r.label}
              </span>
            ))}
        {isStaff && o.dispatched && !o.trackingNumbers?.length && (
          <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">Sent to warehouse</span>
        )}
      </div>
  );
}

export default function Orders() {
  const role = useAuthStore((s) => s.user?.role);
  const isStaff = role === 'INTERNAL_STAFF';
  const activeStore = useStoreContext((s) => s.activeStore); // '' for non-staff
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  // Seed from ?status= so links like the dashboard's "Pending approvals" actually arrive filtered
  // (they used to navigate to /orders?status=PENDING_APPROVAL and land on an unfiltered list).
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [view, setView] = useState('active'); // 'active' | 'archived' (staff)
  const [q, setQ] = useState('');
  const [from, setFrom] = useState(''); // placed on/after (yyyy-mm-dd)
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState('');
  const navigate = useNavigate();

  const archivedView = isStaff && view === 'archived';
  const showClient = role !== 'CLIENT'; // staff + reps work across many clients
  // Arriving from a client ("Orders" on My clients / a client page) filters to that client.
  const companyId = searchParams.get('companyId') || '';
  const filterLabels = FILTER_KEYS.map((k) => ({ key: k, label: k ? orderStatusLabel(k, role) : 'All' }));

  // The same filters drive the list, the pager and the CSV export.
  const filterParams = () => {
    const params = {};
    if (archivedView) params.archived = 'archived';
    else if (status) params.status = status;
    if (activeStore) params.store = activeStore; // scoped by the top-bar store toggle
    if (q.trim()) params.q = q.trim();
    if (companyId) params.companyId = companyId;
    if (from) params.from = new Date(`${from}T00:00:00`).toISOString();
    if (to) params.to = new Date(`${to}T23:59:59.999`).toISOString();
    return params;
  };
  const load = (p = page) => {
    setLoading(true);
    listOrdersPage({ ...filterParams(), page: p, pageSize: PAGE_SIZE })
      .then((r) => {
        setOrders(r.orders);
        setTotal(r.total);
      })
      .catch(() => {
        setOrders([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  };

  // A new filter starts again at page 1.
  useEffect(() => {
    setSelected(new Set());
    setPage(1);
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, activeStore, q, view, from, to, companyId]);
  const goPage = (p) => {
    setSelected(new Set());
    setPage(p);
    load(p);
  };

  // Which rows can be selected: archived view → all (restore); otherwise rejected rows (archive).
  const selectableIds = useMemo(
    () => orders.filter((o) => (archivedView ? true : o.status === 'REJECTED')).map((o) => o.id),
    [orders, archivedView]
  );
  const canSelect = (o) => (archivedView ? true : o.status === 'REJECTED');
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected(() => (allSelected ? new Set() : new Set(selectableIds)));

  const doArchive = async () => {
    setBusy(true);
    setFlash('');
    try {
      const ids = [...selected];
      const res = await archiveOrders(ids);
      const skipped = res.skipped?.length || 0;
      setFlash(`Archived ${res.archived?.length || 0}${skipped ? ` · skipped ${skipped}` : ''}.`);
      setSelected(new Set());
      load();
    } catch {
      setFlash('Could not archive the selection.');
    } finally {
      setBusy(false);
    }
  };
  const doRestore = async () => {
    setBusy(true);
    setFlash('');
    try {
      const res = await restoreOrders([...selected]);
      setFlash(`Restored ${res.restored?.length || 0}.`);
      setSelected(new Set());
      load();
    } catch {
      setFlash('Could not restore the selection.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Orders"
        subtitle={role === 'REP' ? 'Orders for your clients — whoever placed them.' : 'Every wholesale order, with its status and total.'}
        actions={isStaff ? <StoreSwitcher /> : null}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {filterLabels.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => {
              setView('active');
              setStatus(f.key);
              // Keep the URL in step so the filtered view is shareable/bookmarkable and Back works.
              setSearchParams({ ...(f.key ? { status: f.key } : {}), ...(companyId ? { companyId } : {}) }, { replace: true });
            }}
            className={`rounded-full px-3 py-1 text-sm transition ${
              !archivedView && status === f.key ? 'bg-accent text-white' : 'bg-surface-raised text-text-secondary hover:text-text-primary'
            }`}
          >
            {f.label}
          </button>
        ))}
        {isStaff && (
          <>
            <span className="mx-1 h-5 w-px bg-line" aria-hidden />
            <button
              type="button"
              onClick={() => setView(archivedView ? 'active' : 'archived')}
              className={`rounded-full px-3 py-1 text-sm transition ${
                archivedView ? 'bg-accent text-white' : 'bg-surface-raised text-text-secondary hover:text-text-primary'
              }`}
            >
              Archived
            </button>
          </>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-text-muted">
            From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-md border border-line px-2 py-1 text-sm" />
          </label>
          <label className="flex items-center gap-1 text-xs text-text-muted">
            To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md border border-line px-2 py-1 text-sm" />
          </label>
          {role !== 'CLIENT' && (
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Order no., PO or company…"
              aria-label="Search orders"
              className="rounded-md border border-line px-3 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          )}
          <a
            href={ordersCsvUrl(filterParams())}
            className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised"
            title="Download every order matching these filters"
          >
            Export CSV
          </a>
        </div>
      </div>

      {companyId && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="rounded-full bg-accent-light px-3 py-1 text-text-primary">
            Showing one client{orders[0]?.company ? `: ${orders[0].company}` : ''}
          </span>
          <button
            onClick={() => setSearchParams(status ? { status } : {}, { replace: true })}
            className="text-xs text-text-muted hover:text-text-primary hover:underline"
          >
            Show all clients
          </button>
        </div>
      )}

      {/* Bulk action bar (staff) */}
      {isStaff && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-accent/40 bg-accent-light/40 px-4 py-2 text-sm">
          <span className="font-medium text-text-primary">{selected.size} selected</span>
          {archivedView ? (
            <button
              onClick={doRestore}
              disabled={busy}
              className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              {busy ? 'Restoring…' : 'Restore selected'}
            </button>
          ) : (
            <button
              onClick={doArchive}
              disabled={busy}
              className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              {busy ? 'Archiving…' : 'Archive selected'}
            </button>
          )}
          <button onClick={() => setSelected(new Set())} className="text-xs text-text-secondary hover:underline">
            Clear
          </button>
        </div>
      )}
      {flash && <div className="mb-3 text-sm text-text-secondary">{flash}</div>}

      {loading ? (
        <div className="text-sm text-text-muted">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">
          {/* "No orders yet" reads as "this doesn't exist" when you've actually just searched. */}
          {archivedView
            ? 'No archived orders.'
            : q.trim() || status
              ? `No orders match ${q.trim() ? `"${q.trim()}"` : 'this filter'}.`
              : 'No orders yet.'}
        </div>
      ) : (
        <>
        {/* Phones: one tappable card per order instead of a sideways-scrolling table. */}
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface shadow-sm md:hidden">
          {orders.map((o) => (
            <li key={o.id} className="flex items-start gap-3 px-3 py-3">
              {isStaff && canSelect(o) && (
                <input type="checkbox" className="mt-1 accent-accent" checked={selected.has(o.id)} onChange={() => toggle(o.id)} aria-label={`Select ${o.orderNumber || o.id}`} />
              )}
              <button onClick={() => navigate(`/orders/${o.id}`)} className="min-w-0 flex-1 text-left">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-xs text-text-secondary">{o.orderNumber || o.id.slice(-8)}</span>
                  <span className="text-sm font-semibold tabular-nums text-text-primary">{fmtOrderTotal(o)}</span>
                </div>
                {showClient && <div className="truncate text-sm text-text-primary">{o.company || '—'}</div>}
                <div className="mt-1">
                  <OrderChips o={o} isStaff={isStaff} />
                </div>
                <div className="mt-1 text-[11px] text-text-muted">
                  {new Date(o.createdAt).toLocaleDateString()} · {o.unitCount} units
                  {isStaff && ` · ${storeShort(o.store)}`}
                  {o.poNumber && ` · PO ${o.poNumber}`}
                  {o.trackingNumbers?.length > 0 && ` · Tracking ${o.trackingNumbers[0]}`}
                </div>
              </button>
            </li>
          ))}
        </ul>
        <div className="hidden overflow-x-auto rounded-lg border border-line bg-surface shadow-sm md:block">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-line bg-surface-raised/60 text-left text-xs text-text-muted">
                {isStaff && (
                  <th className="w-8 px-3 py-2">
                    <input
                      type="checkbox"
                      className="accent-accent"
                      checked={allSelected}
                      disabled={selectableIds.length === 0}
                      onChange={toggleAll}
                      title={archivedView ? 'Select all' : 'Select all rejected'}
                    />
                  </th>
                )}
                <th className="px-4 py-2 font-medium">Order</th>
                {showClient && <th className="px-4 py-2 font-medium">Client</th>}
                {isStaff && <th className="px-4 py-2 font-medium">Store</th>}
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Tracking</th>
                <th className="px-4 py-2 text-right font-medium">Units</th>
                <th className="px-4 py-2 text-right font-medium">Total</th>
                <th className="px-4 py-2 font-medium">Placed</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => navigate(`/orders/${o.id}`)}
                  className="cursor-pointer border-b border-line last:border-0 hover:bg-surface-raised/40"
                >
                  {isStaff && (
                    <td className="w-8 px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      {canSelect(o) ? (
                        <input
                          type="checkbox"
                          className="accent-accent"
                          checked={selected.has(o.id)}
                          onChange={() => toggle(o.id)}
                        />
                      ) : null}
                    </td>
                  )}
                  <td className="px-4 py-2 font-mono text-xs text-text-secondary">
                    {o.orderNumber || o.id.slice(-8)}
                    {o.poNumber && <div className="font-sans text-[11px] text-text-muted">PO {o.poNumber}</div>}
                  </td>
                  {showClient && <td className="px-4 py-2 text-text-primary">{o.company || '—'}</td>}
                  {isStaff && <td className="px-4 py-2 text-text-secondary">{storeShort(o.store)}</td>}
                  <td className="px-4 py-2">
                    <OrderChips o={o} isStaff={isStaff} />
                  </td>
                  <td className="px-4 py-2 text-xs" onClick={(e) => e.stopPropagation()}>
                    {o.trackingNumbers?.length > 0 ? (
                      <a
                        href={o.trackingUrls?.[0] || '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-accent hover:underline"
                        title={o.trackingNumbers.join(', ')}
                      >
                        {o.trackingNumbers[0]}
                        {o.trackingNumbers.length > 1 ? ` +${o.trackingNumbers.length - 1}` : ''}
                      </a>
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-text-secondary">{o.unitCount}</td>
                  <td className="px-4 py-2 text-right font-medium tabular-nums text-text-primary">
                    {fmtOrderTotal(o)}
                  </td>
                  <td className="px-4 py-2 text-xs text-text-muted">{new Date(o.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      {!loading && <Pager page={page} pageSize={PAGE_SIZE} total={total} onPage={goPage} />}
    </>
  );
}
